import { ReactElement, useState } from 'react';
import { Button, Dialog, DialogTrigger, Popover } from 'react-aria-components';

import { makeStyles } from '@material-ui/core';

import { EmojiPicker } from 'frimousse';

const useStyles = makeStyles(theme => ({
  triggerButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&[data-focus-visible]': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    '& svg': {
      width: 16,
      height: 16,
    },
  },
  popover: {
    backgroundColor: 'var(--background-elevated-base)',
    borderRadius: 'var(--encore-corner-radius-larger)',
    border: '1px solid var(--decorative-subdued)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
    outline: 'none',
    overflow: 'hidden',
    '&[data-entering]': {
      animation: '$fadeIn 150ms ease-out',
    },
    '&[data-exiting]': {
      animation: '$fadeOut 100ms ease-in',
    },
  },
  '@keyframes fadeIn': {
    from: { opacity: 0, transform: 'scale(0.95)' },
    to: { opacity: 1, transform: 'scale(1)' },
  },
  '@keyframes fadeOut': {
    from: { opacity: 1, transform: 'scale(1)' },
    to: { opacity: 0, transform: 'scale(0.95)' },
  },
  dialog: {
    outline: 'none',
  },
  picker: {
    display: 'flex',
    flexDirection: 'column',
    height: 340,
    width: 320,
  },
  search: {
    margin: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    fontSize: 'var(--encore-text-size-smaller)',
    backgroundColor: 'var(--background-elevated-base)',
    border: '1px solid var(--essential-subdued)',
    borderRadius: 'var(--encore-corner-radius-base)',
    color: 'var(--text-base)',
    outline: 'none',
    '&:focus': {
      borderColor: 'var(--essential-base)',
    },
    '&::placeholder': {
      color: 'var(--text-subdued)',
    },
  },
  viewport: {
    flex: 1,
    overflow: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: 'var(--essential-subdued) transparent',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--text-subdued)',
    fontSize: 'var(--encore-text-size-smaller)',
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--text-subdued)',
    fontSize: 'var(--encore-text-size-smaller)',
  },
  list: {
    padding: theme.spacing(0, 1),
    // Style emoji buttons
    '& [frimousse-emoji]': {
      all: 'unset',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      fontSize: 'var(--encore-text-size-base)',
      borderRadius: 'var(--encore-corner-radius-base)',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'var(--background-tinted-highlight)',
      },
      '&:focus': {
        backgroundColor: 'var(--background-tinted-base)',
        outline: 'none',
      },
    },
    // Style category headers
    '& [frimousse-category-header]': {
      padding: theme.spacing(1.5, 0.5, 0.5),
      fontSize: 'var(--encore-text-size-smaller-2)',
      fontWeight: '700',
      color: 'var(--text-subdued)',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      backgroundColor: 'var(--background-elevated-base)',
      zIndex: 1,
    },
    // Style emoji rows
    '& [frimousse-row]': {
      display: 'flex',
      gap: 2,
    },
  },
}));

export interface EmojiPickerPopoverProps {
  icon: ReactElement;
  onEmojiSelect?: (emoji: string) => void;
}

export const EmojiPickerPopover = ({
  icon,
  onEmojiSelect,
}: EmojiPickerPopoverProps) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);

  const handleEmojiSelect = (emoji: { emoji: string }) => {
    onEmojiSelect?.(emoji.emoji);
    setIsOpen(false);
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button
        className={classes.triggerButton}
        aria-label="Add reaction"
        data-track-id="button.emoji-picker"
        data-track-action="expand"
      >
        {icon}
      </Button>
      <Popover placement="top" className={classes.popover}>
        <Dialog className={classes.dialog}>
          <EmojiPicker.Root
            className={classes.picker}
            onEmojiSelect={handleEmojiSelect}
          >
            <EmojiPicker.Search
              className={classes.search}
              placeholder="Search emoji..."
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            <EmojiPicker.Viewport className={classes.viewport}>
              <EmojiPicker.Loading className={classes.loading}>
                Loading…
              </EmojiPicker.Loading>
              <EmojiPicker.Empty className={classes.empty}>
                No emoji found.
              </EmojiPicker.Empty>
              <EmojiPicker.List className={classes.list} />
            </EmojiPicker.Viewport>
          </EmojiPicker.Root>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
};
